import Add from '@mui/icons-material/Add'
import AddCircle from '@mui/icons-material/AddCircleOutlineOutlined'
import Campaign from '@mui/icons-material/CampaignOutlined'
import Description from '@mui/icons-material/DescriptionOutlined'
import EventBusy from '@mui/icons-material/EventBusyOutlined'
import Lock from '@mui/icons-material/LockOutlined'
import PersonAdd from '@mui/icons-material/PersonAddOutlined'
import { Alert, Box, Button, LinearProgress, Link, Paper, Stack, Typography } from '@mui/material'
import type { ComponentChildren, ComponentType } from 'preact'
import { useCallback, useMemo } from 'preact/hooks'
import type { Assignment } from '../api/generated/Assignment'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import { useApi } from '../api/useApi'
import { useEvent } from '../events/EventContext'
import { SectionPage } from '../events/SectionPage'
import { HelpCard } from '../help/HelpCard'
import { InstallGuide } from '../install/InstallGuide'
import { LiveBoard } from '../live/LiveBoard'
import { useLiveReload } from '../live/useLiveReload'
import { PushSetup } from '../notifications/PushSetup'
import { useOrganizerData } from '../organizer/OrganizerData'
import {
  countdown,
  type TodoItem,
  type TodoKind,
  todoItems,
  unreadMessages,
  validatedVolunteers,
  volunteersToAssign,
} from '../organizer/overview'
import { localDate, localDateTime, planningDays } from '../planning/grid'
import { type FillState, fillOf, fillState } from '../shifts/fill'

const todoIcons: Record<TodoKind, ComponentType<{ fontSize?: 'small' }>> = {
  'volunteers-to-assign': PersonAdd,
  'empty-shifts': EventBusy,
  'applications-closed': Lock,
  'no-positions': AddCircle,
  'no-shifts': AddCircle,
  'no-description': Description,
}

const fillColors: Record<FillState, 'success' | 'warning' | 'error'> = {
  full: 'success',
  partial: 'warning',
  empty: 'error',
}

const card = { p: { xs: 1.75, md: 2.5 } } as const

function Figure({
  label,
  value,
  urgent = false,
  children,
}: {
  label: string
  value: string
  urgent?: boolean
  children?: ComponentChildren
}) {
  return (
    <Paper variant="outlined" sx={{ ...card, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography
        component="p"
        sx={{
          fontSize: { xs: 24, md: 30 },
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          color: urgent ? 'warning.dark' : undefined,
        }}
      >
        {value}
      </Typography>
      {children}
    </Paper>
  )
}

function Todo({ items }: { items: TodoItem[] }) {
  return (
    <Paper variant="outlined" sx={{ ...card, gridColumn: { md: 'span 2' } }}>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        À faire
      </Typography>
      {items.length === 0 && (
        <Typography color="text.secondary">Rien à faire pour l'instant.</Typography>
      )}
      {items.map((item, index) => {
        const Icon = todoIcons[item.kind]
        return (
          <Stack
            key={item.key}
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 1.75,
              py: 1.5,
              borderTop: index > 0 ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 38,
                height: 38,
                borderRadius: 2.5,
                bgcolor: item.urgent ? 'warning.light' : 'background.default',
                color: item.urgent ? 'warning.dark' : 'text.secondary',
              }}
            >
              <Icon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {item.detail}
              </Typography>
            </Box>
            <Button
              href={item.href}
              size="small"
              sx={{ flexShrink: 0, bgcolor: 'primary.light', px: 1.75 }}
            >
              {item.action}
            </Button>
          </Stack>
        )
      })}
    </Paper>
  )
}

function PositionFills({
  base,
  positions,
  shifts,
}: {
  base: string
  positions: Position[]
  shifts: Shift[]
}) {
  const rows = positions
    .map((position) => ({
      position,
      fill: fillOf(shifts.filter((shift) => shift.position_id === position.id)),
    }))
    .filter(({ fill }) => fill.required > 0)
  return (
    <Paper variant="outlined" sx={{ ...card, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="h6" component="h2">
          Remplissage par poste
        </Typography>
        <Link href={`${base}/planning`} variant="body2" sx={{ fontWeight: 600 }}>
          Planning
        </Link>
      </Stack>
      {rows.length === 0 && <Typography color="text.secondary">Aucun créneau.</Typography>}
      {rows.map(({ position, fill }) => (
        <Stack key={position.id} spacing={0.75}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
            <Link
              href={`${base}/positions/${position.id}`}
              variant="body2"
              color="text.primary"
              underline="hover"
              noWrap
              sx={{ fontWeight: 600 }}
            >
              {position.name}
            </Link>
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              {fill.accepted} / {fill.required}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={(100 * fill.accepted) / fill.required}
            color={fillColors[fillState(fill)]}
            aria-label={`${position.name} : bénévoles validés`}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Stack>
      ))}
    </Paper>
  )
}

/** Organizers' home: live board, key figures, things to do and fill rate by position. */
export function DashboardPage() {
  const { event } = useEvent()
  const { shifts, conversations, groups, volunteers } = useOrganizerData()
  const base = `/events/${event.slug}`
  const positions = useApi<Position[]>(`${base}/positions`)
  const assignments = useApi<Assignment[]>(`${base}/assignments`)
  const reload = useCallback(() => {
    positions.reload()
    assignments.reload()
  }, [positions.reload, assignments.reload])
  useLiveReload(event.slug, reload)

  const allShifts = shifts.data ?? []
  const days = useMemo(
    () => planningDays(shifts.data ?? [], event),
    [shifts.data, event.starts_on, event.ends_on],
  )
  const fill = fillOf(allShifts)
  const toAssign = volunteers.data && volunteersToAssign(volunteers.data).length
  const unread = unreadMessages(conversations.data ?? [], groups.data ?? [])
  const now = new Date()
  const todo =
    shifts.data &&
    positions.data &&
    volunteers.data &&
    todoItems({
      base,
      positions: positions.data,
      shifts: shifts.data,
      volunteers: volunteers.data,
      applicationsOpen: event.applications_open,
      now: localDateTime(now, event.timezone),
    })
  const overline = [
    countdown(days, localDate(now, event.timezone)),
    event.applications_open ? 'Inscriptions ouvertes' : 'Inscriptions fermées',
  ]
    .filter(Boolean)
    .join(' · ')

  const error = shifts.error ?? volunteers.error ?? positions.error ?? assignments.error
  return (
    <SectionPage
      title="Tableau de bord"
      overline={overline}
      maxWidth="lg"
      actions={
        <>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Campaign />}
            href={`${base}/announcements`}
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, bgcolor: 'background.paper' }}
          >
            Nouvelle annonce
          </Button>
          <Button variant="contained" startIcon={<Add />} href={`${base}/volunteers`}>
            Inviter des bénévoles
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <InstallGuide />
        <PushSetup />
        <LiveBoard slug={event.slug} timezone={event.timezone} />
        <HelpCard />
        {error && <Alert severity="error">{error}</Alert>}
        <Box
          component="section"
          aria-label="Chiffres clés"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: { xs: 1.25, md: 2 },
          }}
        >
          <Figure
            label="Créneaux pourvus"
            value={
              fill.required > 0 ? `${Math.floor((100 * fill.accepted) / fill.required)} %` : '—'
            }
          >
            <LinearProgress
              variant="determinate"
              value={fill.required > 0 ? (100 * fill.accepted) / fill.required : 0}
              aria-label="Places pourvues"
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {fill.accepted} sur {fill.required} places
            </Typography>
          </Figure>
          <Figure
            label="Bénévoles à répartir"
            value={toAssign === undefined ? '—' : String(toAssign)}
            urgent={!!toAssign}
          >
            {!!toAssign && (
              <Link href={`${base}/volunteers`} variant="body2" sx={{ fontWeight: 600 }}>
                Répartir maintenant
              </Link>
            )}
          </Figure>
          <Figure
            label="Bénévoles validés"
            value={assignments.data ? String(validatedVolunteers(assignments.data)) : '—'}
          />
          <Figure label="Messages non lus" value={String(unread)}>
            <Link href={`${base}/messages`} variant="body2" sx={{ fontWeight: 600 }}>
              Ouvrir la messagerie
            </Link>
          </Figure>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {todo && <Todo items={todo} />}
          {shifts.data && positions.data && (
            <PositionFills base={base} positions={positions.data} shifts={shifts.data} />
          )}
        </Box>
      </Stack>
    </SectionPage>
  )
}
